"use client";

import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DiamondIcon from "@mui/icons-material/Diamond";
import FlagIcon from "@mui/icons-material/Flag";
import RedFlagPopup from "../../components/dialog/redFlagPopup";
import FloatPopup from "../../components/dialog/floatPopup";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import WithdrawDialog from "../withdrawalModule/WithdrawDialog";
import ChipToken, { getChipColor } from "../../components/ChipToken";
import { maskPhone } from "../../helper";

const pad = (n) => String(n).padStart(2, "0");
const nowStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const emptyForm = () => ({ date: nowStr(), remarks: "" });

const calcAmount = (denom, qty) => Number(denom || 0) * Number(qty || 0);

const ChipReturnTable = ({ chips, qty, setQty, isPromo = false, onTableChips }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: isPromo ? "#7b1fa208" : "#f5f5f5" }}>
          <TableCell sx={{ fontWeight: 700 }}>Denomination ()</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="center">On Table</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="center">Qty to Return</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {chips.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
              No {isPromo ? "promotional " : ""}chip stock found
            </TableCell>
          </TableRow>
        ) : (
          chips.map((chip) => {
            const q       = qty[chip.denomination] || "";
            const onTable = isPromo
              ? Number(onTableChips[`P${chip.denomination}`] || 0)
              : Number(onTableChips[String(chip.denomination)] || 0);
            const isOver  = Number(q) > onTable;
            const cc = isPromo ? { bg: "#7b1fa2", text: "#fff" } : getChipColor(chip.denomination);
            return (
              <TableRow key={chip._id} sx={{ backgroundColor: cc.bg + "18" }}>
                <TableCell>
                  <ChipToken denomination={chip.denomination} size={40} isPromo={isPromo} />
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={600} color={onTable === 0 ? "error.main" : "text.secondary"}>
                    {onTable}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: 120 }}>
                  <TextField
                    size="small" type="number" value={q}
                    onChange={(e) => setQty({ ...qty, [chip.denomination]: e.target.value })}
                    inputProps={{ min: 0, max: onTable }}
                    disabled={onTable === 0}
                    error={isOver}
                    helperText={isOver ? `Max ${onTable}` : ""}
                    sx={{
                      width: 90,
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
                      "& input[type=number]": { MozAppearance: "textfield" },
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </Paper>
);

export default function ChipCashOutForm({ open, onClose, refresh }) {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const [regularStockChips, setRegularStockChips] = useState([]);
  const [promoStockChips,   setPromoStockChips]   = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [onTableChips, setOnTableChips] = useState({});

  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [repayFloat, setRepayFloat] = useState(true);

  const [returned,      setReturned]      = useState({});
  const [promoReturned, setPromoReturned] = useState({});
  const [form, setForm] = useState({ ...emptyForm() });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitWarnings, setSubmitWarnings] = useState([]);

  const [openWithdraw, setOpenWithdraw] = useState(false);
  const [withdrawUser, setWithdrawUser] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, date: nowStr() }));
    setStockLoading(true);
    Promise.all([
      axiosRequest("GET", `${apiBaseUrl}/chips`, token),
      axiosRequest("GET", `${apiBaseUrl}/shift-management/current-summary`, token),
    ]).then(([chipsRes, summaryRes]) => {
        const chips = Array.isArray(chipsRes?.data) ? chipsRes.data : (chipsRes?.data?.data ?? []);
        const regular = chips.filter((c) => (c.chip_type || "REGULAR") !== "PROMO");
        const promo   = chips.filter((c) => c.chip_type === "PROMO");
        setRegularStockChips(regular);
        setPromoStockChips(promo);

        const initReg = {};
        regular.forEach((c) => { initReg[c.denomination] = ""; });
        setReturned(initReg);

        const initPromo = {};
        promo.forEach((c) => { initPromo[c.denomination] = ""; });
        setPromoReturned(initPromo);

        setOnTableChips(summaryRes?.data?.on_table_chips || {});
      })
      .catch(() => { setRegularStockChips([]); setPromoStockChips([]); })
      .finally(() => setStockLoading(false));
  }, [open]);

  const handleSearch = async () => {
    const input = phone.trim();
    if (!input) return;
    setSearching(true);
    setSearchError("");
    setFoundUser(null);
    try {
      const param = /^\d{10}$/.test(input) ? `phone=${input}` : `playerId=${input}`;
      const res = await axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token);
      const user = res?.data?.userDetails;
      if (user) setFoundUser(user);
      else setSearchError("No user found.");
    } catch {
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const regularAmount = regularStockChips.reduce(
    (sum, c) => sum + calcAmount(c.denomination, returned[c.denomination]), 0
  );
  const promoAmount = promoStockChips.reduce(
    (sum, c) => sum + calcAmount(c.denomination, promoReturned[c.denomination]), 0
  );
  const totalAmount = regularAmount + promoAmount;

  const depositBal      = Number(foundUser?.cash_deposite || 0);
  const winningBal      = Number(foundUser?.winning_balance || 0);
  const loanBal         = Number(foundUser?.loan_amount || 0);
  const loanDistributed = Number(foundUser?.loan_distributed || 0);
  const loanLimit       = Number(foundUser?.loan_limit || 0);
  const floatOutstanding = Number(foundUser?.float_outstanding || 0);

  // Re-arm "recover float" for every player loaded
  useEffect(() => { setRepayFloat(true); }, [foundUser?._id]);

  // Regular chip credit breakdown: repay float first (if opted in), then loan, rest to winning
  const breakFloatRepay = repayFloat ? Math.min(regularAmount, floatOutstanding) : 0;
  const breakLoanRepay = Math.min(regularAmount - breakFloatRepay, loanDistributed);
  const breakToWinning = regularAmount - breakFloatRepay - breakLoanRepay;

  const handleSave = async () => {
    setSubmitError("");
    setSubmitWarnings([]);
    if (!form.remarks.trim()) { setSubmitError("Remarks is required"); return; }
    try {
      setLoading(true);
      const regularChips = regularStockChips
        .filter((c) => Number(returned[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(returned[c.denomination]), chip_type: "REGULAR" }));

      const promoChips = promoStockChips
        .filter((c) => Number(promoReturned[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(promoReturned[c.denomination]), chip_type: "PROMO" }));

      const chips = [...regularChips, ...promoChips];

      const res = await axiosRequest("POST", `${apiBaseUrl}/chip-cashout`, token, {
        player_id: foundUser?.playerId || "",
        user_name: foundUser?.name || "",
        phone: foundUser?.phone || phone,
        ...form,
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        chips,
        total_amount: totalAmount,
        repay_float: repayFloat,
      });

      if (res?.status === false || res?.data?.status === false) {
        setSubmitError(res?.message || res?.data?.message || "Failed to record cash-out.");
        return;
      }

      const warnings = res?.data?.warnings || [];
      refresh();
      if (warnings.length > 0) {
        setSubmitWarnings(warnings);
      }
      if (foundUser) {
        setWithdrawUser(foundUser);
        setOpenWithdraw(true);
      }
      if (warnings.length === 0) handleClose();
    } catch (err) {
      setSubmitError("Something went wrong.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhone("");
    setFoundUser(null);
    setSearchError("");
    setSubmitError("");
    setSubmitWarnings([]);
    setReturned({});
    setPromoReturned({});
    setForm({ ...emptyForm() });
    onClose();
  };

  const isRegularOver = regularStockChips.some(
    (c) => Number(returned[c.denomination] || 0) > Number(onTableChips[String(c.denomination)] || 0)
  );
  const isPromoOver = promoStockChips.some(
    (c) => Number(promoReturned[c.denomination] || 0) > Number(onTableChips[`P${c.denomination}`] || 0)
  );

  return (
    <>
    <WithdrawDialog
      open={openWithdraw}
      onClose={() => { setOpenWithdraw(false); setWithdrawUser(null); }}
      initialUser={withdrawUser}
      hideRedFlagPopup
    />
    <RedFlagPopup user={open ? foundUser : null} />
    <FloatPopup user={open ? foundUser : null} context="cashout" big />
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 640, p: 3, overflowY: "auto", height: "100%" }}>

        {/* HEADER */}
        <Typography variant="h5" fontWeight={700}>Chip Cash-Out</Typography>
        <Typography color="text.secondary" variant="body2" mb={2}>
          Record chips returned by a player and credit their wallet
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* PHONE SEARCH */}
        <Typography fontWeight={600} mb={1}>Search Player</Typography>
        <Box display="flex" gap={1} mb={1}>
          <TextField
            fullWidth size="small" label="Phone / Player ID"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained" onClick={handleSearch}
            disabled={searching || !phone.trim()}
            sx={{ minWidth: 90, whiteSpace: "nowrap" }}
          >
            {searching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Box>

        {searchError && <Alert severity="error" sx={{ mb: 1 }}>{searchError}</Alert>}

        {/* Red flag warning */}
        {foundUser?.red_flag && (
          <Alert severity="error" icon={<FlagIcon />} sx={{ mb: 2, fontWeight: 600 }}>
            RED FLAG — {foundUser.red_flag_remark || "This player has been flagged"}
          </Alert>
        )}

        {/* USER CARD */}
        {foundUser && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
            <Typography fontWeight={700} variant="subtitle1">{foundUser.name || "—"}</Typography>
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Player ID</Typography>
                <Typography fontWeight={600} variant="body2">{foundUser.playerId || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Phone</Typography>
                <Typography fontWeight={600} variant="body2">{maskPhone(foundUser.phone) || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Winning Balance</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#38ef7d" }}>
                  {winningBal.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Promo Balance</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#ce93d8" }}>
                  {Number(foundUser?.promotional_balance || 0).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12}><Box sx={{ borderTop: "1px solid rgba(255,255,255,0.15)", my: 1 }} /></Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Deposited</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#7ec8e3" }}>
                  {depositBal.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Outstanding</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: loanBal > 0 ? "#ff6b6b" : "#38ef7d" }}>
                  {loanBal.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Limit</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#fff" }}>
                  {loanLimit.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Distributed</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: loanDistributed > 0 ? "#f59e0b" : "#fff" }}>
                  {loanDistributed.toLocaleString()}
                </Typography>
              </Grid>
              {floatOutstanding > 0 && (
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Float Outstanding</Typography>
                  <Typography fontWeight={700} variant="h6" sx={{ color: "#ffb74d" }}>
                    {floatOutstanding.toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* REGULAR CHIP RETURN TABLE */}
        <Typography fontWeight={700} mb={1}>Regular Chips to Return</Typography>
        {stockLoading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : (
          <ChipReturnTable chips={regularStockChips} qty={returned} setQty={setReturned} isPromo={false} onTableChips={onTableChips} />
        )}

        {/* PROMO CHIP RETURN TABLE */}
        {!stockLoading && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DiamondIcon sx={{ color: "#7b1fa2", fontSize: 20 }} />
              <Typography fontWeight={700}>Promotional Chips to Return</Typography>
              <Chip label="PROMO" size="small" sx={{ bgcolor: "#7b1fa2", color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              <Typography variant="caption" color="text.secondary">— credits Promo Balance only</Typography>
            </Box>
            <ChipReturnTable chips={promoStockChips} qty={promoReturned} setQty={setPromoReturned} isPromo={true} onTableChips={onTableChips} />
          </>
        )}

        {/* TOTAL SUMMARY */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, background: "linear-gradient(135deg,#11998e,#38ef7d)", color: "#fff" }}>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Cash-Out Amount</Typography>
          <Typography variant="h5" fontWeight={700}>{totalAmount.toLocaleString()}</Typography>
          {promoAmount > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              (Regular: {regularAmount.toLocaleString()} + Promo: {promoAmount.toLocaleString()})
            </Typography>
          )}
        </Paper>

        {/* FLOAT RECOVERY — optional */}
        {foundUser && floatOutstanding > 0 && (
          <Paper variant="outlined" sx={{ mb: 2, px: 2, py: 1, borderRadius: 2, borderColor: repayFloat ? "#e65100" : "divider" }}>
            <FormControlLabel
              control={<Switch checked={repayFloat} onChange={(e) => setRepayFloat(e.target.checked)} color="warning" />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>Recover float from this cash-out</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {repayFloat
                      ? `Float outstanding ${floatOutstanding.toLocaleString()} is repaid first`
                      : `Float skipped — ${floatOutstanding.toLocaleString()} stays outstanding`}
                  </Typography>
                </Box>
              }
            />
          </Paper>
        )}

        {/* CREDIT BREAKDOWN */}
        {foundUser && totalAmount > 0 && (
          <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
            {promoAmount > 0 && (
              <Box display="flex" justifyContent="space-between" alignItems="center"
                sx={{ px: 2, py: 1.2, borderBottom: "1px solid #f0f0f0", bgcolor: "#7b1fa208" }}>
                <Typography variant="body2" fontWeight={600} color="#7b1fa2">Promo Chips → Promo Balance</Typography>
                <Typography fontWeight={700} color="#7b1fa2">+ {promoAmount.toLocaleString()}</Typography>
              </Box>
            )}
            {[
              { label: "1. Float Repayment", amount: breakFloatRepay, color: "#e65100" },
              { label: "2. Loan Repayment",  amount: breakLoanRepay,  color: "#e53935" },
              { label: "3. Winning Wallet",  amount: breakToWinning,  color: "#388e3c" },
            ].map(({ label, amount, color }) => (
              <Box key={label} display="flex" justifyContent="space-between" alignItems="center"
                sx={{ px: 2, py: 1.2, borderBottom: "1px solid #f0f0f0" }}>
                <Typography variant="body2" fontWeight={600} color={color}>{label}</Typography>
                <Typography fontWeight={700} color={amount > 0 ? color : "text.disabled"}>
                  {amount > 0 ? `+ ${amount.toLocaleString()}` : "—"}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* OTHER FIELDS */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} size="small" label="Remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              required
              error={!form.remarks.trim()}
              helperText={!form.remarks.trim() ? "Remarks is required" : ""}
            />
          </Grid>

          {submitError && (
            <Grid item xs={12}><Alert severity="error">{submitError}</Alert></Grid>
          )}

          {submitWarnings.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ fontWeight: 600 }}>
                <strong>Denomination Overflow Warning</strong>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                  {submitWarnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained" color="success" onClick={handleSave}
                disabled={
                  loading || !foundUser || !form.date || totalAmount === 0 || !form.remarks.trim() ||
                  isRegularOver || isPromoOver
                }
              >
                {loading ? "Saving..." : "Cash Out"}
              </Button>
            </Box>
          </Grid>
        </Grid>

      </Box>
    </Drawer>
    </>
  );
}
